import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getContractDetails } from "~/features/contracts/api/getContractDetails";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";
import { POPUP_SET_INITIAL_VALUES } from "~/features/contracts/redux/actions";
import type { AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { requestAvailabilityDataSingle } from "~/domains/product/api/availabilityService";
import Spinner from "~/common/components/Spinner/Spinner";
import { Stack } from "~/common/mui";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import NewContractDetailsSummary from "~/features/contracts/components/ContractDetails/ContractDetailsSummary";
import NewDeliveriesTable from "~/features/contracts/components/ContractDetails/DeliveriesTable";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { Alert } from "~/common/mui/Alert/Alert";

interface NewContractDetailsPageProps {
  contractNo: string;
  contractPosition: number | null;
  materialNo: string;
  packageCode: string;
}

const ContractDetailsPage = ({
  contractNo,
  contractPosition,
  materialNo,
  packageCode,
}: NewContractDetailsPageProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [contractDetails, setContractDetails] = useState<ContractDetailsResponse>();
  const [productAvailability, setProductAvailability] = useState<AvailabilityInfo>();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    getContractDetails(contractNo, materialNo, packageCode, contractPosition)
      .then((contractDetail) => {
        if (cancelled) {
          return;
        }

        dispatch(addBaseProductDataAction(contractDetail.product));
        setContractDetails(contractDetail);
        dispatch({
          type: POPUP_SET_INITIAL_VALUES,
          payload: { qty: contractDetail.nextDispositionQuantity, rdd: contractDetail.nextDisposition },
        });
        requestAvailabilityDataSingle({ productCode: contractDetail.product.code })
          .then((availabilityData) => {
            if (!cancelled) {
              setProductAvailability(availabilityData);
            }
          })
          .catch(() => undefined);
      })
      .catch(() => {
        if (!cancelled) {
          setHasError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (hasError) {
    return (
      <Alert className="mt-3" severity="error">
        {t("generic.error.ajax")}
      </Alert>
    );
  }

  if (!contractDetails) {
    return <Spinner />;
  }

  return (
    <div>
      <PageTitle
        clta-details-page-element-marker="title"
        clta-contract-id={contractDetails.customerContractNumber}
      >{`${t("newContract.contractTile")} ${contractDetails.customerContractNumber}`}</PageTitle>

      <Stack gap={4}>
        <NewContractDetailsSummary contractDetails={contractDetails} productAvailability={productAvailability} />
        <NewDeliveriesTable contractLines={contractDetails?.contractLines} />
      </Stack>
    </div>
  );
};

export default ContractDetailsPage;

interface ContractDetailsPageDTO {
  contractNo: string;
  contractPosition: string | null;
  materialNo: string;
  packageCode: string;
}

export function creatContractDetailsPage(data: ContractDetailsPageDTO) {
  const position = data.contractPosition ? +data.contractPosition : null;

  return <ContractDetailsPage {...data} contractPosition={position} />;
}
